import React, { useEffect, useState } from 'react';

interface UniversalPaginationCallbackProps {
  totalPages: number;
  currentPage: number;
  loading?: boolean;
  onPageChange: (page: number) => void; // Povinný callback pro změnu stránky
  showPageNumbers?: boolean; // Zda zobrazit čísla stránek
  maxVisiblePages?: number; // Maximální počet viditelných stránek
  className?: string; // Custom CSS třídy
}

const UniversalPaginationCallback: React.FC<UniversalPaginationCallbackProps> = ({
  totalPages,
  currentPage,
  loading = false,
  onPageChange,
  showPageNumbers = true,
  maxVisiblePages = 5,
  className = '',
}) => {
  const [internalPage, setInternalPage] = useState(currentPage);

  // Sync internal state with props
  useEffect(() => {
    setInternalPage(currentPage);
  }, [currentPage]);

  // Handle page change - pouze volá callback, neovlivňuje URL
  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages || page === internalPage || loading) {
      return;
    }

    setInternalPage(page);
    
    // Pouze volá callback - rodičovská komponenta si sama rozhodne, co udělat
    onPageChange(page);

    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle previous page
  const handlePrevious = () => {
    handlePageChange(internalPage - 1);
  };

  // Handle next page
  const handleNext = () => {
    handlePageChange(internalPage + 1);
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    if (totalPages <= maxVisiblePages) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const half = Math.floor(maxVisiblePages / 2);
    let start = Math.max(1, internalPage - half);
    let end = Math.min(totalPages, start + maxVisiblePages - 1);

    if (end - start + 1 < maxVisiblePages) {
      start = Math.max(1, end - maxVisiblePages + 1);
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  if (totalPages <= 1) {
    return null;
  }

  return (
    <div className={`flex justify-center items-center gap-2 ${className}`}>
      {/* Previous button */}
      <button
        onClick={handlePrevious}
        disabled={internalPage === 1 || loading}
        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300 transition-colors"
        title="Předchozí stránka"
      >
        Předchozí
      </button>

      {/* Page numbers */}
      {showPageNumbers && (
        <div className="flex gap-1">
          {getPageNumbers().map((pageNum) => (
            <button
              key={pageNum}
              onClick={() => handlePageChange(pageNum)}
              disabled={loading}
              className={`px-3 py-2 rounded-lg transition-colors ${
                pageNum === internalPage
                  ? 'bg-blue-500 text-white shadow-sm'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
              title={`Stránka ${pageNum}`}
            >
              {pageNum}
            </button>
          ))}
        </div>
      )}

      {/* Next button */}
      <button
        onClick={handleNext}
        disabled={internalPage === totalPages || loading}
        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300 transition-colors"
        title="Další stránka"
      >
        Další
      </button>

      {/* Page info */}
      <div className="ml-4 text-sm text-gray-600">
        {internalPage} / {totalPages}
      </div>
    </div>
  );
};

export default UniversalPaginationCallback;
